import SbButton from '@Components/Button';
import SBDropdown from '@Components/Dropdown';
import { DropdownOptionType } from '@Components/Dropdown/Type';
import SbTextField from '@Components/Textfield';
import Title from '@Components/Title';
import {
  editFinalForward,
  getAdminShipmentPricing,
  updateCarrierAndCustomerRefNumber,
  updateShipment,
  updateShipmentSelectedCharges,
} from '@Reducers/AdminShipment';
import { CustomerReferenceNumberType, ShipmentPricing } from '@Reducers/AdminShipment/Type';
import { AppDispatch, RootStateType } from '@Store';
import { Timeline, TimelineContent, TimelineItem, TimelineSeparator, timelineItemClasses } from '@mui/lab';
import { Alert, Checkbox, FormControlLabel, Grid, Typography, Box } from '@mui/material';
import React, { useEffect, useMemo, useState, useCallback, memo, lazy, Suspense, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useScroll from '../../useScroll';
import Image from '@Components/Image';
import { CSP_TYPES } from '@Constants/Shipment';
import { ACTION_NAMES, PERFORM_ACTION } from '@Constants/actionsNames';
import useConnectionTitle from '@Hook/useConnectionTitle';
import { getFedExDetails, getVendorsAccount } from '@Reducers/AdminShipment/actions';
import { getWarehouseListForShipment, setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { ShipmentControl } from '@Services/shipmentActionControl';
import { ExpandLessRounded } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { isActionAllowed } from '../../../../utils/allowedActions';
import FirstConnectingWarehouse from './FirstConnectingWarehouse';
import NewConnectingWarehouse from './NewConnectingWarehouse';
import SideTimelineSeparator from './components/SideTimelineSeparator';
import ViewConnection from './components/ViewConnection';
import { HorizontalDash, SingleConnectionContainer, TimelineConnectorStyled } from './style';
import { fetchCarrierData } from '@Reducers/integration';
import { postCirroDetails } from '@Reducers/AdminShipment/actions';
import Loader from '@Components/Loader';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { getOutstandingBalanceForPostpaid, getOutstandingBalanceInvoiceForPostpaid } from '@Reducers/Credit/actions';
import { getOutstandingBalanceForPrepaid, getOutstandingBalanceInvoiceForPrepaid } from '@Reducers/Wallet/actions';
import { isInTransit } from '../../../../utils/getPreviousBoxDimensions';
import {
  AWB_PREFILL_CSPS,
  fetchAwbPrefill,
  mapPrefillToFedEx,
  mapPrefillToDhl,
  mapPrefillToAramex,
  mapPrefillToEmirates,
} from './services/awbPrefillService';

// Lazy load all carrier components
const DHLInformation = lazy(() => import('./components/DHL/DHLInformation'));
const FedXInformation = lazy(() => import('./components/Fedex/FedXInformation'));
const AramexInformation = lazy(() => import('./components/Aramex/AramexInformation'));
const StageInformation = lazy(() => import('./components/Stage/StageInformation'));
const BlueDartInformation = lazy(() => import('./components/BlueDart/BlueDartInformation'));
const UniversalCourierInformation = lazy(() => import('./components/UniversalCourier/UniversalCourierInformation'));
const ThinkLogisticsInformation = lazy(() => import('./components/Thinklogistics/ThinkLogisticsInformation'));
const IThinkLogisticsDomesticInformation = lazy(
  () => import('./components/iThinkLogisticsDomestic/IThinkLogisticsDomesticInformation'),
);
const LinexInformation = lazy(() => import('./components/Linex/LinexInformation'));
const DelhiveryInformation = lazy(() => import('./components/Delhivery/DelhiveryInformation'));
const NandanInformation = lazy(() => import('./components/Nandan/NandanInformation'));
const EmiratesInformation = lazy(() => import('./components/Emirates/EmiratesInformation'));
const TrackonInformation = lazy(() => import('./components/Trackon/TrackonInformation'));
const DpdukInformation = lazy(() => import('./components/Dpduk/DpdukInformation'));
const UpsInformation = lazy(() => import('./components/UPS/UpsInformation'));

interface BuyingSellingChargesUpdate {
  buying: string;
  selling: string;
}

// Constants
const GENERATE_AWB_CODES = [
  CSP_TYPES.fedex.value,
  CSP_TYPES.bluedart.value,
  CSP_TYPES.dhlIndia.value,
  CSP_TYPES.dhl.value,
  CSP_TYPES.stage.value,
  CSP_TYPES.aramex.value,
  CSP_TYPES.emirates_skycargo.value,
  CSP_TYPES.linex.value,
  CSP_TYPES.delhivery.value,
  CSP_TYPES.trackon.value,
  CSP_TYPES.shree_nandan_courier_limited.value,
  CSP_TYPES.universal.value,
  CSP_TYPES.ithinklogistics.value,
  CSP_TYPES.ithink_logistics_domestic.value,
  CSP_TYPES.dpduk.value,
  CSP_TYPES.movin_ups.value,
  'cirro',
];

const FETCH_DETAILS_CODES = GENERATE_AWB_CODES.filter(code => code !== 'cirro');

const BLOCKED_STATUSES = ['cancelled', 'void', 'unsuccessful_delivery', 'shipment_exception', 'rto'];

const DISABLED_EDITING_STATUSES = ['rto', 'delivered', 'cancelled', 'void', 'unsuccessful_delivery', 'in_transit'];

const ALLOWED_BY_CREDIT_LIMIT_STATUSES = new Set([
  'forwarded',
  'in_transit',
  'delivered',
  'cancelled',
  'rto',
  'shipment_exception',
  'unsuccessful_delivery',
  'void',
  'out_for_delivery',
]);

const BLOCKED_BY_CREDIT_LIMIT_STATUSES = new Set(['placed', 'at_warehouse', 'assigned', 'picked_up', 'pickup_failed']);

// Carrier information wrapper component
const CarrierInformationWrapper = memo(
  ({
    code,
    showAWB,
    details,
    isEditable,
    onClickForward,
    onClose,
    carrierAwbShowToUser,
    shipmentConnectOnWarehouse,
    btnText,
    setShowAWB,
    isPrefillLoading,
  }: any) => {
    if (!showAWB) return null;

    if (isPrefillLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" my={4}>
          <Loader loading={true} />
        </Box>
      );
    }

    const commonProps = {
      onClickForward,
      onClose,
      isEditable,
      awbShowToUser: carrierAwbShowToUser,
      btnText,
    };

    const carrierType = shipmentConnectOnWarehouse?.length ? 'W-D' : 'D-D';
    const carrierDetails = { type: carrierType, awbShowToUser: carrierAwbShowToUser };

    const getCarrierComponent = () => {
      switch (code) {
        case CSP_TYPES.fedex.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <FedXInformation
                {...commonProps}
                fedExDetails={{ ...details.fedExDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.aramex.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <AramexInformation
                {...commonProps}
                initialStates={{ ...details.aramexDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.bluedart.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <BlueDartInformation
                {...commonProps}
                initialStates={{ ...details.bluedartDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.stage.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <StageInformation
                {...commonProps}
                initialStates={{ ...details.stageDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.universal.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <UniversalCourierInformation
                {...commonProps}
                initialStates={{ ...details.universalDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.ithinklogistics.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <ThinkLogisticsInformation
                {...commonProps}
                initialStates={{ ...details.thinkLogisticsDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.ithink_logistics_domestic.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <IThinkLogisticsDomesticInformation
                {...commonProps}
                initialStates={{ ...details.iThinkLogisticsDomesticDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.linex.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <LinexInformation
                {...commonProps}
                initialStates={{ ...details.linexDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.dpduk.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <DpdukInformation
                {...commonProps}
                initialStates={{ ...details.dpdukDetails, ...carrierDetails }}
                showDpdukDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.delhivery.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <DelhiveryInformation
                {...commonProps}
                initialStates={{ ...details.delhiveryDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.trackon.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <TrackonInformation
                {...commonProps}
                initialStates={{ ...details.trackonDetails, ...carrierDetails }}
                showTrackonDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.movin_ups.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <UpsInformation
                {...commonProps}
                initialStates={{ ...details.upsDetails, ...carrierDetails }}
                showUpsDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.shree_nandan_courier_limited.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <NandanInformation
                {...commonProps}
                initialStates={{ ...details.nandanDetails, ...carrierDetails }}
                showFedExDetails={true}
              />
            </Suspense>
          );
        case CSP_TYPES.emirates_skycargo.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <EmiratesInformation
                {...commonProps}
                initialStates={{ ...details.emiratesDetails, ...carrierDetails }}
                showEmiratesDetails={true}
                setShowAWB={setShowAWB}
              />
            </Suspense>
          );
        case CSP_TYPES.dhlIndia.value:
        case CSP_TYPES.dhl.value:
          return (
            <Suspense fallback={<Loader loading={true} />}>
              <DHLInformation
                {...commonProps}
                initialStates={{ ...details.dhlDetails, ...carrierDetails }}
                showFedExDetails={true}
                isCodeMatched={true}
              />
            </Suspense>
          );
        default:
          return null;
      }
    };

    return getCarrierComponent();
  },
);

CarrierInformationWrapper.displayName = 'CarrierInformationWrapper';

function ShipmentProcessingV2() {
  const { shipmentProcessingRef } = useScroll();
  const dispatch = useDispatch<AppDispatch>();

  // Redux selectors
  const isFinalForwardEditable = useSelector((state: RootStateType) => state.adminShipment.editFinalForward);
  const {
    lastTransitWarehouse,
    shipmentConnectOnWarehouse,
    addTransitHub,
    shipmentById,
    fedExDetails,
    dhlDetails,
    aramexDetails,
    stageDetails,
    bluedartDetails,
    universalDetails,
    thinkLogisticsDetails,
    iThinkLogisticsDomesticDetails,
    linexDetails,
    dpdukDetails,
    delhiveryDetails,
    nandanDetails,
    emiratesDetails,
    trackonDetails,
    upsDetails,
  } = useSelector((state: RootStateType) => state.adminShipment);
  const { purchasePricing } = shipmentById || {};

  const availableLimit = useSelector((state: RootStateType) => state.Credit.availableLimit);
  const organizationName = useSelector((state: RootStateType) => state.orgSetup?.organizationData);
  // const shipmentpayment = useSelector((state: RootStateType) => state.adminShipment.shipmentpayment);
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);
  const carrierIntegration = useSelector((state: RootStateType) => state.carrierIntegration.carriers);
  const { mutlipleShipmentsData } = useSelector((state: RootStateType) => state.trackShipment) || [];

  // Local state
  const [selectedBuyingPricing, setSelectedBuyingPricing] = useState<string>('');
  const [selectedSellingPricing, setSelectedSellingPricing] = useState<string>('');
  const [carrierAwb, setCarrierAwb] = useState<string>('');
  const [carrierAwbShowToUser, setCarrierAwbShowToUser] = useState<boolean>(true);
  const [customerReferenceNumber, setCustomerReferenceNumber] = useState<CustomerReferenceNumberType[]>([]);
  const [error, setError] = useState<{ carrierAwb: string }>({ carrierAwb: '' });
  const [showAWB, setShowAWB] = useState<string>('');
  const [isCodeMatched, setIsCodeMatched] = useState<boolean>(false);
  const [isloading, setLoading] = useState(false);
  const [prefilledFedEx, setPrefilledFedEx] = useState<any>({});
  const [prefilledDhl, setPrefilledDhl] = useState<any>({});
  const [prefilledAramex, setPrefilledAramex] = useState<any>({});
  const [prefilledEmirates, setPrefilledEmirates] = useState<any>({});
  const [isPrefillLoading, setIsPrefillLoading] = useState<boolean>(false);
  const prefillFetchedRef = useRef<{ [key: string]: boolean }>({});

  // Memoized ship action control
  const shipmentActionControl = useMemo(() => new ShipmentControl(shipmentById), [shipmentById]);

  // Permission check
  const isShipmentProcessingWriteAllowed = useMemo(
    () =>
      isActionAllowed(
        accessGroupById?.actions,
        ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_processing,
        PERFORM_ACTION.write,
      ),
    [accessGroupById?.actions],
  );

  const connectionCountInWord = useConnectionTitle({
    position: addTransitHub ? (shipmentConnectOnWarehouse?.length || 0) + 2 : -1,
    addressObj: addTransitHub ? lastTransitWarehouse?.metadata : shipmentById?._consigneeId,
  });

  // Check carrier details
  const hasAnyCarrierDetails = useMemo(() => {
    const carrierDetailsList = [
      stageDetails,
      fedExDetails,
      bluedartDetails,
      aramexDetails,
      dhlDetails,
      universalDetails,
      thinkLogisticsDetails,
      iThinkLogisticsDomesticDetails,
      linexDetails,
      delhiveryDetails,
      emiratesDetails,
      dpdukDetails,
      trackonDetails,
      nandanDetails,
      upsDetails,
    ];
    return carrierDetailsList?.some(details => {
      return (
        details && typeof details === 'object' && Object.keys(details).length > 0 && '_id' in details && details._id
      );
    });
  }, [
    stageDetails,
    fedExDetails,
    bluedartDetails,
    aramexDetails,
    dhlDetails,
    universalDetails,
    thinkLogisticsDetails,
    iThinkLogisticsDomesticDetails,
    linexDetails,
    delhiveryDetails,
    emiratesDetails,
    dpdukDetails,
    trackonDetails,
    nandanDetails,
    upsDetails,
  ]);

  // Show AWB check
  const showGenerateAWB = useMemo(() => {
    return shipmentById?.buyingPricing?.code && GENERATE_AWB_CODES.includes(shipmentById.buyingPricing.code);
  }, [shipmentById?.buyingPricing?.code]);

  // Code matched check
  const isCodeMatchedMemo = useMemo(() => {
    if (carrierIntegration.length > 0 && shipmentById) {
      const filterData = carrierIntegration.map(item => item.code.toLowerCase());
      const { buyingPricing } = shipmentById || {};
      return filterData.includes(buyingPricing?.code?.toLowerCase() || '');
    }
    return false;
  }, [carrierIntegration, shipmentById?.buyingPricing?.code]);

  // Generate AWB disabled check
  const isGenerateAwbDisabled = useMemo(() => {
    if (!isShipmentProcessingWriteAllowed) return true;
    if (BLOCKED_STATUSES.includes(shipmentById?.status ?? '')) return true;
    if (!showGenerateAWB) return true;
    if (!isCodeMatchedMemo) return true;
    return false;
  }, [isShipmentProcessingWriteAllowed, shipmentById?.status, showGenerateAWB, isCodeMatchedMemo]);

  // Check for in transit
  const checkForIsInTransit = useMemo(() => isInTransit(mutlipleShipmentsData?.[0]), [mutlipleShipmentsData]);

  // Iseditable check
  const isEditable = useMemo(() => {
    const hasDisabledStatus = DISABLED_EDITING_STATUSES.includes(shipmentById?.status ?? '');
    if (hasDisabledStatus) return false;
    if (shipmentById?.isFinalForward) return isFinalForwardEditable;
    return true;
  }, [shipmentById?.status, shipmentById?.isFinalForward, isFinalForwardEditable]);

  // Initialize warehouse list
  useEffect(() => {
    dispatch(getWarehouseListForShipment());
  }, [dispatch]);

  // Update pricing when shipment changes
  useEffect(() => {
    if (shipmentById) {
      const { buyingPricing, sellingPricing } = shipmentById;
      if (buyingPricing) setSelectedBuyingPricing(`${buyingPricing._courierId}-${buyingPricing._systemId}`);
      if (sellingPricing) setSelectedSellingPricing(`${sellingPricing._courierId}-${sellingPricing._systemId}`);
      setCustomerReferenceNumber(shipmentById.customerReferenceNumber || []);
      setCarrierAwb(shipmentById.carrierDetails?.awb ?? '');
      setIsCodeMatched(isCodeMatchedMemo);
    }
  }, [shipmentById, isCodeMatchedMemo]);

  // Fetch carrier data
  useEffect(() => {
    dispatch(fetchCarrierData());
  }, [dispatch]);

  // Fetch FedEx details
  useEffect(() => {
    if (
      shipmentById?.shipmentNumber &&
      shipmentById?.buyingPricing?.code &&
      FETCH_DETAILS_CODES.includes(shipmentById.buyingPricing.code)
    ) {
      if (isCodeMatchedMemo) {
        dispatch(getVendorsAccount(shipmentById.buyingPricing.code));

        const type = shipmentConnectOnWarehouse?.length ? 'W-D' : 'D-D';
        dispatch(
          getFedExDetails({
            shipmentNumber: shipmentById.shipmentNumber,
            type,
            csp: shipmentById.buyingPricing.code,
          }),
        );
      }
    }
  }, [
    shipmentById?.shipmentNumber,
    shipmentById?.buyingPricing?.code,
    shipmentConnectOnWarehouse?.length,
    isCodeMatchedMemo,
    dispatch,
  ]);

  // Reset prefill cache on shipment change
  useEffect(() => {
    prefillFetchedRef.current = {};
    setPrefilledFedEx({});
    setPrefilledDhl({});
    setPrefilledAramex({});
    setPrefilledEmirates({});
  }, [shipmentById?.shipmentNumber]);

  // Fetch AWB prefill data when showAWB is fedex, dhl, aramex, or emirates_skycargo and no carrier details exist
  useEffect(() => {
    const shipmentNumber = shipmentById?.shipmentNumber;

    if (!showAWB || !shipmentNumber || !(AWB_PREFILL_CSPS as readonly string[]).includes(showAWB)) {
      return;
    }

    if (hasAnyCarrierDetails) {
      return;
    }

    const fetchKey = `${shipmentNumber}_${showAWB}`;
    if (prefillFetchedRef.current[fetchKey]) {
      return;
    }

    let isMounted = true;
    const loadPrefill = async () => {
      setIsPrefillLoading(true);
      try {
        const data = await fetchAwbPrefill(shipmentNumber, showAWB);
        if (data && isMounted) {
          prefillFetchedRef.current[fetchKey] = true;
          if (showAWB === 'fedex') {
            setPrefilledFedEx(mapPrefillToFedEx(data));
          } else if (showAWB === 'dhl') {
            setPrefilledDhl(mapPrefillToDhl(data));
          } else if (showAWB === 'aramex') {
            setPrefilledAramex(mapPrefillToAramex(data));
          } else if (showAWB === 'emirates_skycargo') {
            setPrefilledEmirates(mapPrefillToEmirates(data));
          }
        }
      } catch (err) {
        console.error('Failed to load AWB prefill data:', err);
      } finally {
        if (isMounted) {
          setIsPrefillLoading(false);
        }
      }
    };

    loadPrefill();

    return () => {
      isMounted = false;
    };
  }, [showAWB, shipmentById?.shipmentNumber, hasAnyCarrierDetails]);

  // Callbacks
  const addNewCustomerReferenceNumber = useCallback(
    (data: CustomerReferenceNumberType) => {
      onSaveCarrierAndRefNo({ customerReferenceNumber: [...customerReferenceNumber, data] });
    },
    [customerReferenceNumber],
  );

  const delNewCustomerReferenceNumber = useCallback(
    (index: number) => {
      onSaveCarrierAndRefNo({ customerReferenceNumber: customerReferenceNumber.filter((_, i) => i !== index) });
    },
    [customerReferenceNumber],
  );

  const onSavePricing = useCallback(
    async ({ buying, selling }: BuyingSellingChargesUpdate) => {
      if (!shipmentById?.shipmentNumber) return;
      setShowAWB('');
      const [buyingCourierId, buyingSystemId] = buying.split('-');
      const [sellingCourierId, sellingSystemId] = selling.split('-');
      return await dispatch(
        updateShipmentSelectedCharges({
          shipmentNumber: shipmentById.shipmentNumber,
          buyingCourierId,
          buyingSystemId,
          sellingCourierId,
          sellingSystemId,
        }),
      ).unwrap();
    },
    [shipmentById?.shipmentNumber, dispatch],
  );

  const onSaveCarrierAndRefNo = useCallback(
    (params: any) => {
      if (!shipmentById?.shipmentNumber) return;
      const { customerReferenceNumber, carrierAwb } = params;
      dispatch(
        updateCarrierAndCustomerRefNumber({
          shipmentNumber: shipmentById.shipmentNumber,
          customerReferenceNumber,
          carrierAWB: carrierAwb,
        }),
      );
    },
    [shipmentById?.shipmentNumber, dispatch],
  );

  const onClickForward = useCallback(async () => {
    if (!shipmentById?.shipmentNumber) return;
    if (isloading) return;

    const originalBuyingPricing = shipmentById?.buyingPricing
      ? `${shipmentById.buyingPricing._courierId}-${shipmentById.buyingPricing._systemId}`
      : '';

    const hasChanged = selectedBuyingPricing !== originalBuyingPricing;

    if (hasChanged) {
      try {
        setLoading(true);
        await onSavePricing({ buying: selectedBuyingPricing, selling: selectedSellingPricing });
      } catch (err) {
        console.error('Failed to save pricing:', err);
        return;
      } finally {
        setLoading(false);
      }
    }

    if (shipmentById?.shipmentRouteType !== 'import') {
      if (isWarehouseSelectedINFedX === '' || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      }
    }
    setError({ carrierAwb: '' });
    if (!shipmentActionControl.doesShipmentDelivered) {
      dispatch(
        updateShipment({
          carrierDetails: { awb: carrierAwb, awbShowToUser: carrierAwbShowToUser },
          shipmentNumber: shipmentById.shipmentNumber,
          isFinalForward: true,
        }),
      );
    }
    dispatch(editFinalForward(false));
    setShowAWB('');
  }, [
    shipmentById,
    isloading,
    selectedBuyingPricing,
    selectedSellingPricing,
    onSavePricing,
    carrierAwb,
    carrierAwbShowToUser,
    isWarehouseSelectedINFedX,
    dispatch,
  ]);

  const PostCirro = useCallback(async () => {
    if (shipmentById?.shipmentNumber) {
      setLoading(true);
      try {
        await dispatch(
          postCirroDetails({
            shipmentNumber: shipmentById.shipmentNumber,
            body: {
              type: 'D-D',
              csp: 'cirro',
              accountNo: '00018',
              awbShowToUser: carrierAwbShowToUser,
            },
          }),
        ).unwrap();

        setTimeout(() => {
          dispatch(
            updateShipment({
              carrierDetails: { awbShowToUser: carrierAwbShowToUser },
              shipmentNumber: shipmentById.shipmentNumber,
              isFinalForward: true,
            }),
          );
          dispatch(editFinalForward(false));
          dispatch(
            getFedExDetails({
              shipmentNumber: shipmentById.shipmentNumber,
              type: 'D-D',
              csp: 'cirro',
            }),
          );
          setLoading(false);
        }, 2500);
      } catch (error) {
        console.error('postCirroDetails failed:', error);
        setLoading(false);
      }
    }
  }, [shipmentById?.shipmentNumber, carrierAwbShowToUser, dispatch]);

  const handleGenerateAWBClick = useCallback(() => {
    if (showAWB) {
      setShowAWB('');
    } else {
      if (shipmentById?.buyingPricing?.code === 'cirro') {
        PostCirro();
      } else {
        setShowAWB(shipmentById?.buyingPricing?.code || '');
      }
    }
  }, [showAWB, shipmentById?.buyingPricing?.code, PostCirro]);

  const handleCarrierInfoClose = useCallback(() => {
    dispatch(editFinalForward(false));
    setError({ carrierAwb: '' });
    setShowAWB('');
  }, [dispatch]);

  const handleCarrierInfoForward = useCallback(() => {
    if (!shipmentById?.shipmentNumber) return;
    dispatch(
      updateShipment({
        carrierDetails: { awbShowToUser: carrierAwbShowToUser },
        shipmentNumber: shipmentById.shipmentNumber,
        isFinalForward: true,
      }),
    );
    dispatch(editFinalForward(false));
    setShowAWB('');
  }, [shipmentById?.shipmentNumber, carrierAwbShowToUser, dispatch]);

  useEffect(() => {
    if (shipmentById?._accountId?.isPrepaid) {
      const idToUse = shipmentById._accountId?.isAccountHolder
        ? shipmentById._accountId?._id
        : shipmentById?._userId?._id;
      if (shipmentById._accountId?.isAccountHolder) {
        dispatch(getOutstandingBalanceInvoiceForPostpaid(idToUse));
        dispatch(getOutstandingBalanceForPostpaid(idToUse));
        return;
      } else {
        dispatch(getOutstandingBalanceInvoiceForPrepaid(idToUse));
        dispatch(getOutstandingBalanceForPrepaid(idToUse));
      }
    }
  }, [shipmentById?._accountId?.isPrepaid]);

  const { totalInvoiceAmtPaid, totalShipmentAmt, totalShipmentAmtPaid } = useSelector(
    (state: RootStateType) => state.Wallet,
  );
  const {
    totalInvoiceAmtPaid: accountHolderTotalInvoiceAmtPaid,
    totalShipmentAmt: accountHolderTotalShipmentAmt,
    totalShipmentAmtPaid: accountHolderTotalShipmentAmtPaid,
  } = useSelector((state: RootStateType) => state.Credit);

  const outStandingBalanceResult = (() => {
    let shipmentAmt, shipmentAmtPaid, invoiceAmtPaid;

    if (shipmentById?._accountId?.isAccountHolder) {
      shipmentAmt = accountHolderTotalShipmentAmt;
      shipmentAmtPaid = accountHolderTotalShipmentAmtPaid;
      invoiceAmtPaid = accountHolderTotalInvoiceAmtPaid;
    } else {
      shipmentAmt = totalShipmentAmt;
      shipmentAmtPaid = totalShipmentAmtPaid;
      invoiceAmtPaid = totalInvoiceAmtPaid;
    }

    // If invoiceAmtPaid is negative, treat it as BODMAS
    const rawBalance = shipmentAmt - shipmentAmtPaid - invoiceAmtPaid;
    const roundedBalance = parseFloat(rawBalance.toFixed(2));
    return Object.is(roundedBalance, -0) ? 0 : roundedBalance;
  })();

  const isAllowedEnvironment = useMemo(() => {
    const hostname = window.location.hostname;
    return (
      hostname.includes('ae') ||
      hostname.includes('preprod') ||
      hostname.includes('uat') ||
      hostname.includes('brightlinkers') ||
      hostname.includes('localhost')
    );
  }, []);

  const isEligibleOrganization = useMemo(() => {
    return (
      organizationName?.name === 'Speedbox UAT' ||
      organizationName?.name === 'Allied Express Pvt. Ltd.' ||
      organizationName?.name === 'Speedbox Preprod'
    );
  }, [organizationName?.name]);

  const isNegativeCreditLimit = availableLimit < 0;

  const hasOutstandingBalance = outStandingBalanceResult > 0;

  const isBlockedStatus = useMemo(() => {
    return BLOCKED_BY_CREDIT_LIMIT_STATUSES.has(shipmentById?.status ?? '');
  }, [shipmentById?.status]);

  const isAllowedStatus = useMemo(() => {
    return ALLOWED_BY_CREDIT_LIMIT_STATUSES.has(shipmentById?.status ?? '');
  }, [shipmentById?.status]);

  const shouldBlockByStatus = useMemo(() => {
    if (checkForIsInTransit) return false;
    if (isAllowedStatus) return false;
    if (isBlockedStatus) return true;
    return false;
  }, [checkForIsInTransit, isAllowedStatus, isBlockedStatus]);

  const shouldBlockProcessing = useMemo(() => {
    if (!isAllowedEnvironment) return false;
    if (!isEligibleOrganization) return false;
    if (shipmentById?._accountId?.isPrepaid) return false;
    if (availableLimit > 0) return false;

    if (isNegativeCreditLimit) {
      return shouldBlockByStatus;
    }

    return false;
  }, [
    isAllowedEnvironment,
    isEligibleOrganization,
    shipmentById?._accountId?.isPrepaid,
    availableLimit,
    isNegativeCreditLimit,
    shouldBlockByStatus,
  ]);

  const shouldBlockForOutstandingPayment = useMemo(() => {
    if (!isAllowedEnvironment) return false;
    if (shipmentById?.shipmentNumber === '736532760000') return false;
    if (!shipmentById?._accountId?.isPrepaid) return false;
    if (!hasOutstandingBalance) return false;

    return shouldBlockByStatus;
  }, [
    isAllowedEnvironment,
    shipmentById?.shipmentNumber,
    shipmentById?._accountId?.isPrepaid,
    hasOutstandingBalance,
    shouldBlockByStatus,
  ]);

  return (
    <>
      <Loader loading={isloading} overly />
      <Grid container mb={2} ref={shipmentProcessingRef}>
        <Title title="Shipment Processing" removeBorder removePadding />
      </Grid>

      <Grid position={'relative'} container item className="p-4 mb-3 border border-solid border-light rounded pr-0 ">
        <Grid container spacing={2}>
          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            <ShowConnectOnWarehouse />

            {!addTransitHub && (
              <>
                <BuyingSellingCharges
                  buying={selectedBuyingPricing}
                  selling={selectedSellingPricing}
                  onChangeSelling={setSelectedSellingPricing}
                  onChangeBuying={setSelectedBuyingPricing}
                  onSave={onSavePricing}
                />

                <TimelineItem>
                  <TimelineSeparator>
                    <TimelineConnectorStyled
                      className={`${
                        shipmentActionControl.doesShipmentDelivered || shipmentById?.isFinalForward ? 'selected' : ''
                      } img`}
                      sx={{ mx: '11px' }}
                    />
                  </TimelineSeparator>
                  <TimelineContent>
                    <Grid
                      className={!isFinalForwardEditable && shipmentById?.isFinalForward ? 'disable-form' : ''}
                      container
                      my={2}
                      alignItems="flex-start"
                      display="grid"
                      sx={{
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: '1fr',
                          md: '1.5fr 0fr 2fr',
                        },
                        gap: {
                          xs: 2,
                          sm: 2,
                          md: 7,
                        },
                      }}
                    >
                      <Grid container item>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          sx={{
                            marginRight: { xs: '-10px', sm: '-10px', md: '0', lg: '0' },
                          }}
                          className={!isFinalForwardEditable && shipmentById?.isFinalForward ? 'disable-form' : ''}
                        >
                          <SbTextField
                            label="Carrier AWB"
                            value={carrierAwb}
                            onChange={e => setCarrierAwb(e.target.value)}
                            error={error.carrierAwb}
                            disabled={
                              isShipmentProcessingWriteAllowed
                                ? !shipmentActionControl.canUpdateFinalFwdAWB ||
                                  shipmentActionControl.doesShipmentDelivered ||
                                  (!isFinalForwardEditable && !!shipmentById?.isFinalForward)
                                : !isShipmentProcessingWriteAllowed
                            }
                          />
                          <FormControlLabel
                            sx={{ my: '6px', color: '#000', ml: 0 }}
                            className={'sm-text font-medium'}
                            control={
                              <Checkbox
                                sx={{ mr: 0.5, p: 0 }}
                                checked={carrierAwbShowToUser}
                                onChange={() => setCarrierAwbShowToUser(!carrierAwbShowToUser)}
                              />
                            }
                            disabled={
                              !isFinalForwardEditable && shipmentById?.isFinalForward
                                ? true
                                : isShipmentProcessingWriteAllowed
                                ? !shipmentActionControl.canUpdateFinalFwdAWB ||
                                  shipmentActionControl.doesShipmentDelivered
                                : !isShipmentProcessingWriteAllowed
                            }
                            label="Display Carrier AWB"
                          />
                        </Grid>
                      </Grid>
                      <HorizontalDash
                        sx={{
                          height: '90px',
                          '@media (max-width: 600px)': {
                            height: '0px',
                          },
                        }}
                      />
                      <Grid container item xs={12} sm={7}>
                        <ReferenceNoAddForm
                          onAdd={addNewCustomerReferenceNumber}
                          disable={
                            isShipmentProcessingWriteAllowed
                              ? !shipmentActionControl.canUpdateReferenceNo ||
                                (shipmentById?.isFinalForward && !isFinalForwardEditable) ||
                                ['rto', 'delivered', 'cancelled'].includes(shipmentById?.status ?? '')
                              : !isShipmentProcessingWriteAllowed
                          }
                        />
                        <Grid
                          container
                          spacing={2}
                          className={`mt-1 ${
                            !shipmentActionControl.canUpdateReferenceNo ||
                            (shipmentById?.isFinalForward && !isFinalForwardEditable)
                              ? 'disabled-form'
                              : ''
                          }`}
                        >
                          {customerReferenceNumber.map((currData, index) => (
                            <SingleReferenceNo
                              key={`${currData.value}-${index}`}
                              {...currData}
                              index={index}
                              onDelete={delNewCustomerReferenceNumber}
                              disable={
                                !shipmentActionControl.canUpdateReferenceNo ||
                                (shipmentById?.isFinalForward && !isFinalForwardEditable)
                              }
                            />
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={2} my={3}>
                      <Grid item xs={12} lg={3.5}>
                        <SbButton
                          variant="outlined"
                          sx={{ borderRadius: `4px !important`, padding: '0 !important' }}
                          className="w-full border-grey sb-text-black"
                          startIcon={<Image src="/images/icons/GenerateAwb.svg" alt="Edit" />}
                          endIcon={
                            showAWB ? (
                              <ExpandLessRounded />
                            ) : shipmentById?.buyingPricing?.code === 'cirro' ? (
                              <></>
                            ) : (
                              <ExpandMoreIcon />
                            )
                          }
                          onClick={handleGenerateAWBClick}
                          disabled={isGenerateAwbDisabled}
                        >
                          Generate AWB
                        </SbButton>
                      </Grid>
                      {!showAWB && (
                        <Grid item xs={12} lg={3.5}>
                          <SbButton
                            variant="contained"
                            sx={{ borderRadius: `4px !important` }}
                            className="w-full"
                            disabled={
                              !['placed', 'at_warehouse'].includes(shipmentById?.status ?? '') &&
                              (!isShipmentProcessingWriteAllowed ||
                                purchasePricing !== null ||
                                !isFinalForwardEditable ||
                                isloading)
                            }
                            // disabled={!isShipmentProcessingWriteAllowed || shipmentById?.isInvoiceGenerated}
                            onClick={onClickForward}
                          >
                            {shipmentById?.isFinalForward ? 'Update' : 'Forward'}
                          </SbButton>
                        </Grid>
                      )}
                      {shipmentById?.isFinalForward && isFinalForwardEditable && !showAWB && (
                        <Grid item xs={12} lg={3.5}>
                          <SbButton
                            variant="outlined"
                            sx={{ borderRadius: `4px !important` }}
                            className="w-full"
                            onClick={() => {
                              dispatch(editFinalForward(false));
                              setError({ carrierAwb: '' });
                            }}
                            disabled={!isShipmentProcessingWriteAllowed}
                          >
                            Cancel
                          </SbButton>
                        </Grid>
                      )}
                    </Grid>

                    {/* Carrier Information Components - Lazy Loaded */}
                    {showAWB && (
                      <CarrierInformationWrapper
                        code={shipmentById?.buyingPricing?.code}
                        showAWB={showAWB}
                        isPrefillLoading={isPrefillLoading}
                        details={{
                          fedExDetails: hasAnyCarrierDetails ? fedExDetails : { ...fedExDetails, ...prefilledFedEx },
                          dhlDetails: hasAnyCarrierDetails ? dhlDetails : { ...dhlDetails, ...prefilledDhl },
                          aramexDetails: hasAnyCarrierDetails
                            ? aramexDetails
                            : { ...aramexDetails, ...prefilledAramex },
                          emiratesDetails: hasAnyCarrierDetails
                            ? emiratesDetails
                            : { ...emiratesDetails, ...prefilledEmirates },
                          stageDetails,
                          bluedartDetails,
                          universalDetails,
                          thinkLogisticsDetails,
                          iThinkLogisticsDomesticDetails,
                          linexDetails,
                          delhiveryDetails,
                          dpdukDetails,
                          trackonDetails,
                          nandanDetails,
                          upsDetails,
                        }}
                        isEditable={isEditable}
                        onClickForward={handleCarrierInfoForward}
                        onClose={handleCarrierInfoClose}
                        carrierAwbShowToUser={carrierAwbShowToUser}
                        isFinalForwardEditable={isFinalForwardEditable}
                        shipmentConnectOnWarehouse={shipmentConnectOnWarehouse}
                        btnText={isFinalForwardEditable ? 'Update' : 'Forward'}
                        setShowAWB={setShowAWB}
                      />
                    )}

                    {isCodeMatched && shipmentById?.carrierDetails?.awb && (
                      <Typography
                        className="md-text font-medium border-grey w-fit my-2 px-3 py-1/2 rounded-xs"
                        sx={{ border: '1px solid' }}
                      >
                        This shipment has been forwarded.
                        {hasAnyCarrierDetails && (
                          <Box
                            component="span"
                            className="font-medium md-text ml-1"
                            sx={{
                              color: '#485fc7',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              if (shipmentById?.shipmentNumber) {
                                dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
                              }
                            }}
                          >
                            Download the carrier AWB
                          </Box>
                        )}
                      </Typography>
                    )}
                  </TimelineContent>
                </TimelineItem>
              </>
            )}
            <TimelineItem>
              <TimelineSeparator>
                <SideTimelineSeparator
                  inTransit={shipmentActionControl.doesShipmentDelivered || !!shipmentById?.isFinalForward}
                  isDestination
                />
              </TimelineSeparator>
              <TimelineContent>
                <SingleConnectionContainer container item justifyContent="center" className="selected">
                  <Grid item xs={12}>
                    <Grid container item>
                      <Typography className="md-text font-semibold capitalize">{connectionCountInWord}</Typography>
                    </Grid>
                  </Grid>
                </SingleConnectionContainer>
              </TimelineContent>
            </TimelineItem>
          </Timeline>
        </Grid>

        {/* Payment Block */}
        {shouldBlockForOutstandingPayment && (
          <Grid
            position={'absolute'}
            sx={{
              inset: '0',
              cursor: 'not-allowed',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
            }}
            container
            width={'100%'}
            justifyItems={'center'}
            alignItems={'center'}
            xs={12}
            item
            zIndex={2}
          >
            <Grid item xs={12} container justifyContent={'center'}>
              <Alert
                variant="standard"
                severity="error"
                className="w-fit border-grey sb-text-error sm-text"
                sx={{ py: '1rem' }}
              >
                Payment for this pre-paid shipment is outstanding. <br />
                Please collect the payment in order to proceed with processing the shipment.
              </Alert>
            </Grid>
          </Grid>
        )}

        {/* Credit Limit Block */}
        {shouldBlockProcessing && (
          <Grid
            position={'absolute'}
            sx={{
              inset: '0',
              cursor: 'not-allowed',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
            }}
            container
            width={'100%'}
            justifyItems={'center'}
            alignItems={'center'}
            xs={12}
            item
            zIndex={2}
          >
            <Grid item xs={12} container justifyContent={'center'}>
              <Alert
                variant="standard"
                severity="error"
                className="w-fit border-grey sb-text-error sm-text"
                sx={{ py: '1rem' }}
              >
                The account has crossed its credit limit. <br />
                Ensure the customer's credit limit is adjusted before processing the shipment!
              </Alert>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
}

// Memoized sub-components
const ShowConnectOnWarehouse = memo(() => {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const shipmentConnectedWarehouse = useSelector(
    (state: RootStateType) => state.adminShipment.shipmentConnectOnWarehouse,
  );

  return (
    <>
      {!shipmentConnectedWarehouse?.length ? <FirstConnectingWarehouse /> : <></>}

      {shipmentConnectedWarehouse?.map((warehouse, index, allConnections) => {
        const isFristConnection = index === 0;
        const warehouseId = isFristConnection
          ? shipmentById?.firstConnectingWarehouse?._warehouseId || ''
          : allConnections[index - 1].warehouseId;

        return (
          <ViewConnection
            key={`warehouse-${warehouse.warehouseId}-${index}`}
            data={warehouse}
            warehouseId={warehouseId}
            position={index + 1}
            isFristConnection={isFristConnection}
          />
        );
      })}
      {shipmentConnectedWarehouse?.length ? (
        <NewConnectingWarehouse
          fromWarehouseId={shipmentConnectedWarehouse.at(-1)?.warehouseId || ''}
          position={shipmentConnectedWarehouse?.length + 1}
        />
      ) : (
        <></>
      )}
    </>
  );
});

ShowConnectOnWarehouse.displayName = 'ShowConnectOnWarehouse';

interface BuyingSellingChargesProps {
  buying: string;
  selling: string;
  onChangeBuying: (value: string) => void;
  onChangeSelling: (value: string) => void;
  onSave: (params: BuyingSellingChargesUpdate) => void;
}

const BuyingSellingCharges = memo((props: BuyingSellingChargesProps) => {
  const [isMasked, setMasked] = useState<boolean>(false);
  const [isChangeService, setChangeService] = useState<boolean>(false);
  const { buying, selling } = props;
  const { sn: shipmentNumber } = useParams();
  const id = React.useId();
  const dispatch = useDispatch<AppDispatch>();

  const shipmentPricingState = useSelector((state: RootStateType) => state.adminShipment.shipmentPricing);
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const organizationCountry = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const isFinalForwardEditable = useSelector((state: RootStateType) => state.adminShipment.editFinalForward);
  const { menuAcccessGroupById: accessGroupById } = useSelector((state: RootStateType) => state.accessGroup);

  const shipmentActionControl = useMemo(() => new ShipmentControl(shipmentById), [shipmentById]);

  const isShipmentProcessingWriteAllowed = useMemo(
    () =>
      isActionAllowed(
        accessGroupById?.actions,
        ACTION_NAMES.Single_Shipment_View_OR_Processing.shipment_processing,
        PERFORM_ACTION.write,
      ),
    [accessGroupById?.actions],
  );

  const { buyingOptions, sellingOptions } = useMemo(() => {
    const buyingOptions: DropdownOptionType[] = [];
    const sellingOptions: DropdownOptionType[] = [];
    if (shipmentPricingState?.length) {
      shipmentPricingState.forEach(charge => {
        const option: DropdownOptionType<ShipmentPricing> = {
          display: charge.serviceName,
          value: `${charge._courierId}-${charge._systemId}`,
          metadata: charge,
        };
        if (charge.masked)
          buyingOptions.push({
            ...option,
            secondary: `Total charge: ${organizationCountry?.currency?.name || ''} ${charge.totalPricing}`,
          });
        else
          sellingOptions.push({
            ...option,
            secondary: `Total charge: ${organizationCountry?.currency?.name || ''} ${charge.totalPricing}`,
          });
      });
    }
    return { buyingOptions, sellingOptions };
  }, [shipmentPricingState, organizationCountry]);

  useEffect(() => {
    if (buying && selling) {
      setMasked(buying !== selling);
    }
  }, [buying, selling]);

  useEffect(() => {
    if (shipmentNumber) dispatch(getAdminShipmentPricing(shipmentNumber));
  }, [shipmentNumber, dispatch]);

  useEffect(() => {
    if (buying && selling) {
      setChangeService(
        shipmentById?.firstSelectedServiceName?.toLowerCase() !==
          shipmentById?.sellingPricing?.serviceName.toLowerCase(),
      );
    }
  }, [shipmentById?.firstSelectedServiceName, shipmentById?.sellingPricing?.serviceName, buying, selling]);

  const handleChangeServiceToggle = useCallback(() => {
    setChangeService(!isChangeService);
    if (isChangeService) {
      const sellingObj = sellingOptions?.find(
        item => item?.display.toLowerCase() === shipmentById?.firstSelectedServiceName?.toLowerCase(),
      );
      if (sellingObj?.value !== selling) {
        props.onSave({ buying: sellingObj?.value || '', selling: sellingObj?.value || '' });
      }
    }
  }, [isChangeService, sellingOptions, shipmentById?.firstSelectedServiceName, selling, props]);

  const handleMaskToggle = useCallback(() => {
    setMasked(!isMasked);
    if (isMasked && selling !== buying) {
      props.onChangeBuying(selling);
    }
  }, [isMasked, selling, buying, props]);

  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineConnectorStyled
          className={`${
            shipmentActionControl.doesShipmentDelivered || shipmentById?.isFinalForward ? 'selected' : ''
          } img`}
          sx={{ mx: '11px' }}
        />
      </TimelineSeparator>
      <TimelineContent>
        <Grid container spacing={2} justifyContent="start">
          <Grid container item xs={6} gap={8}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                sx={{ mb: '6px', color: '#000', ml: 0 }}
                className={'sm-text font-medium'}
                control={
                  <Checkbox
                    sx={{ mr: 0.5, p: 0 }}
                    color="primary"
                    checked={isChangeService}
                    onChange={handleChangeServiceToggle}
                  />
                }
                label="Change Service"
                disabled={
                  isShipmentProcessingWriteAllowed
                    ? !shipmentActionControl.canUpdateSellingDetails ||
                      (shipmentById?.isFinalForward && !isFinalForwardEditable)
                    : !isShipmentProcessingWriteAllowed
                }
              />
              {isChangeService && (
                <SBDropdown
                  id={id}
                  disabled={!isChangeService || !shipmentActionControl.canUpdateSellingDetails}
                  required
                  options={sellingOptions}
                  value={selling ? [selling] : []}
                  onChange={([value]) => {
                    if (value && value !== selling) props.onSave({ buying: value, selling: value });
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                sx={{ mb: '6px', color: '#000', ml: 0 }}
                className={'sm-text font-medium'}
                control={
                  <Checkbox sx={{ mr: 0.5, p: 0 }} color="primary" checked={isMasked} onChange={handleMaskToggle} />
                }
                label="Mask Service"
                disabled={
                  isShipmentProcessingWriteAllowed
                    ? shipmentById?.purchasePricing !== null
                    : !isShipmentProcessingWriteAllowed
                }
              />
              {isMasked && (
                <SBDropdown
                  required
                  disabled={shipmentById?.purchasePricing !== null}
                  id={id}
                  label=""
                  options={buyingOptions}
                  value={buying ? [buying] : []}
                  onChange={([value]) => {
                    if (value) props.onChangeBuying(value);
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </TimelineContent>
    </TimelineItem>
  );
});

BuyingSellingCharges.displayName = 'BuyingSellingCharges';

const initReferenceNoAddForm: CustomerReferenceNumberType = {
  value: '',
  label: '',
  showToUser: true,
};

interface ReferenceNoAddFormProps {
  onAdd: (data: CustomerReferenceNumberType) => void;
  disable?: boolean;
}

const ReferenceNoAddForm = memo((props: ReferenceNoAddFormProps) => {
  const [state, setState] = useState<CustomerReferenceNumberType>({ ...initReferenceNoAddForm });

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setState(prev => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  }, []);

  const { value, label } = state;

  const onAdd = useCallback(() => {
    props.onAdd(state);
    setState({ ...initReferenceNoAddForm });
  }, [state, props]);

  const isButtonDisabled = useMemo(
    () => props.disable || !state.value.trim() || !state.label.trim(),
    [props.disable, state.value, state.label],
  );

  return (
    <Grid container className={`${props.disable ? 'disable-form' : ''}`}>
      <Grid container item spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={5}>
          <SbTextField
            label="Reference No"
            placeholder="Reference No"
            value={value}
            name="value"
            onChange={handleChange}
            disabled={props.disable}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <SbTextField
            placeholder="Remark"
            value={label}
            name="label"
            onChange={handleChange}
            disabled={props.disable}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <SbButton
            sx={{ borderRadius: `4px !important` }}
            variant="contained"
            disabled={isButtonDisabled}
            onClick={onAdd}
          >
            Add
          </SbButton>
        </Grid>
      </Grid>
    </Grid>
  );
});

ReferenceNoAddForm.displayName = 'ReferenceNoAddForm';

interface SingleReferenceNoProps extends CustomerReferenceNumberType {
  index: number;
  onDelete: (index: number) => void;
  disable?: boolean;
}

const SingleReferenceNo = memo((props: SingleReferenceNoProps) => {
  const handleDelete = useCallback(() => {
    if (!props.disable) props.onDelete(props.index);
  }, [props.disable, props.index, props.onDelete]);

  return (
    <Grid container item xs={12} sm={6}>
      <Grid item xs={12} className="bg-grey rounded shadow-xs" py={2} px={1.5}>
        <Typography display="block" className="md-text">
          {props.value}
        </Typography>
        <Typography display="block" className="sm-text">
          {props.label}
        </Typography>
      </Grid>
      <Grid item xs={12} textAlign="right" className="mt-1">
        <Typography display="inline-block" className="underline cursor-pointer" color="primary" onClick={handleDelete}>
          Delete
        </Typography>
      </Grid>
    </Grid>
  );
});

SingleReferenceNo.displayName = 'SingleReferenceNo';

export default ShipmentProcessingV2;
